import React, { forwardRef, useImperativeHandle, useRef, useState } from "react";
import { RCCGrid, RCCHookToast, RCCInput, RCCRefModalProps, RCCServicesApi, RCCSwitch } from "react-common-components";
import { useForm } from "react-hook-form";
import Modal from "src/components/Modal";
import { useTranslation } from "src/context/Translations/context";
import { getValidationErrors, standardErrorMessages } from "src/utils/errors";
import { useLoaderBackdrop } from "src/context/LoaderBackdrop/context";
import { useConfirmModal } from "src/context/ConfirmModal/context";
import { ModalCenter } from "src/components/Modal/styles";
import { IModalIncomeStatementChangeCopyDelete, IOftbm001, IRefIncomeStatementModals } from "./interface";
import copyAndChangeSchema from "./schemas";

const ModalsIncomeStatementChangeCopyDelete: React.ForwardRefRenderFunction<IRefIncomeStatementModals, IModalIncomeStatementChangeCopyDelete> = ({
	budgetPlanId,
	onCallback,
	type
},ref) => {
	const { translate } = useTranslation();
	const {addToast} = RCCHookToast();
	const {confirm} = useConfirmModal();
	const {setLoading} = useLoaderBackdrop();

	const [incomeSelected, setIncomeStatementSelected] = useState<IOftbm001>({} as IOftbm001);

	// Modal Add/Change
	const modalAddOrChange = useRef<RCCRefModalProps>(null);
	const isCopy = useRef(false);
	const { 
		handleSubmit : handleSubmitAddOrChange, 
		register : registerAddOrChange,
		reset : resetAddOrChange, 
		setError : setErrorAddOrChange, 
		formState: { errors : errorsAddOrChange }, 
		clearErrors: clearErrorsAddOrChange
	} = useForm();

	const actionButtonCopy = async (id: number) => {
		confirm(
			translate("Copiar demonstrativo"),
			`${translate("Deseja realmente copiar este demonstrativo")}?`,
			async () => {
				isCopy.current = true;
				resetAddOrChange(); 
				const copyString = translate("CÓPIA");
				setLoading(true);
				const result = await RCCServicesApi.postRequest({
					method: "IncomeStatement/Copy",
					urlParameters: {
						id,
						budgetPlanId
					}
				});
				setIncomeStatementSelected({
					...result.data,
					ofm001Descricao: `${copyString} ${result.data.ofm001Descricao.substring(0, result.data.ofm001Descricao.length - 7)}`
				});
				setLoading(false);
				addToast({
					color: "blue",
					description: translate("Demonstrativo copiado, você pode alterá-lo agora"),
					title: translate("Atenção")
				});
				modalAddOrChange.current?.openModal();
			},
			(err) => {
				setLoading(false);
				standardErrorMessages(err, translate("Erro ao copiar demonstrativo"), addToast);
			},
			false
		);
	};

	const actionButtonChange = (id: number) => {
		resetAddOrChange(); 
		isCopy.current = false;
		getIncomeStatement(id);
	};

	const submitAddOrChange = async (data: any) => {
		try{
			clearErrorsAddOrChange();
			modalAddOrChange.current?.setLoadingButton();
			
			await copyAndChangeSchema(translate).validate({...data}, {
				abortEarly: false
			});
			const dataForm = {
				...data,
				ofm001Padrao: data.ofm001Padrao ? "S" : "N",
				ofm001Ofa001Id: budgetPlanId,
				ofm001Tipo: type
			};

			await RCCServicesApi.putRequest({
				method: "IncomeStatement/Edit",
				body: {
					...dataForm
				}
			});

			addToast({
				color: "green",
				description: translate("Demonstrativo alterado com sucesso"),
				title: translate("Sucesso")
			});

			onCallback();
			modalAddOrChange.current?.resetLoadingButton();
			modalAddOrChange.current?.closeModal();

		}catch(err){

			modalAddOrChange.current?.resetLoadingButton();
			if(getValidationErrors(err, setErrorAddOrChange)){
				addToast({
					color: "yellow",
					description: translate("Verifique os campos e tente novamente"),
					title: translate("Atenção")
				});
				return;
			}
				
			standardErrorMessages(err, translate("Erro ao alterar demonstrativo"), addToast);
		}
	};

	const actionButtonDelete = async (id: number) => {
		confirm(
			translate("Excluir DRE"),
			`${translate("Deseja realmente excluir este demonstrativo")}?`,
			async () => {
				await RCCServicesApi.deleteRequest({
					method: "IncomeStatement/Delete",
					urlParameters: {
						id,
						budgetPlanId
					}
				});
    
				addToast({
					color: "green",
					description: translate("Demonstrativo excluído com sucesso"),
					title: translate("Sucesso")
				});

				onCallback();
			},
			(err) => {
				standardErrorMessages(err, translate("Erro ao excluir demonstrativo"), addToast);
			},
			true
		);
	};

	const actionButtonChangeType = async (id: number) => {
		confirm(
			translate("Alterar tipo do demonstrativo"),
			`${translate("Deseja realmente alterar o tipo deste demonstrativo")}?`,
			async () => {
				await RCCServicesApi.putRequest({
					method: "IncomeStatement/ChangeType",
					urlParameters: {
						id,
						budgetPlanId
					}
				});
    
				addToast({
					color: "green",
					description: translate("Demonstrativo alterado com sucesso"),
					title: translate("Sucesso")
				});

				onCallback();
			},
			(err) => {
				standardErrorMessages(err, translate("Erro ao alterar demonstrativo"), addToast);
			},
			false
		);
	};

	useImperativeHandle(ref, () => ({
		actionButtonCopy,
		actionButtonChange,
		actionButtonDelete,
		actionButtonChangeType
	}));

	async function getIncomeStatement(idIncomeStatement: number){
		try{
			setLoading(true);
			const result = await RCCServicesApi.getRequest({
				method: "IncomeStatement/Select",
				urlParameters: {
					id: idIncomeStatement,
					budgetPlanId
				}
			});
			setLoading(false);
			setIncomeStatementSelected(result.data);
			modalAddOrChange.current?.openModal();
		}catch(err){
			setLoading(false);
			standardErrorMessages(err, translate("Erro ao buscar demonstrativo"), addToast);
		}
	}

	return (
		<Modal
			ref={modalAddOrChange}
			header={{
				title: translate("Alterar DRE"),
				icon: {
					name: "dollar"
				}
			}}
			onSubmit={handleSubmitAddOrChange(submitAddOrChange)}
			onClose={() => {
				if(isCopy.current)
					onCallback();
			}}
			styleInline={`${ModalCenter} height: 280px;`}
			withoutForm
		>
			<RCCGrid container item direction="column" alignItems="center">
				<input 
					type="text"
					value={incomeSelected.ofm001Id}
					{...registerAddOrChange("ofm001Id")} 
					style={{ display: "none" }} 
					readOnly
				/>
				<RCCGrid container item xs={12} sm={10}>
					<RCCInput 
						name="ofm001Descricao"
						register={registerAddOrChange}
						label={translate("Descrição")}
						maxLength={100}
						defaultValue={incomeSelected.ofm001Descricao}
						error={errorsAddOrChange.ofm001Descricao}
					/>
				</RCCGrid>
				<RCCGrid container item xs={12} sm={10}>
					<RCCSwitch 
						name="ofm001Padrao"
						register={registerAddOrChange}
						label={translate("Padrão")}
						defaultChecked={incomeSelected.ofm001Padrao === "S"}		
					/>
				</RCCGrid>
			</RCCGrid>
		</Modal>
	);
};

export default forwardRef(ModalsIncomeStatementChangeCopyDelete);
